import { useFormik } from 'formik'
import ActionTextContentField from 'FlowBuilder/components/ActionViews/ActionTextContentField'
import ServerWaitButton from 'Shared/components/ServerWaitButton'
import Loading from 'Shared/components/Loading'
import { getValidationSchema, transformQuestionsWithTargetDetails } from 'FlowTaker/lib/formHelpers'
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate'

function getQuestions(requiredUserInfo, placeholders) {
  let questions = [
    { id: 'name', questionType: 'name', label: '', placeholder: placeholders.namePlaceholder, settings: { required: true } },
    { id: 'email', questionType: 'email', label: '', placeholder: placeholders.emailPlaceholder, settings: { required: true } }
  ]

  if (requiredUserInfo === 'name_email_postcode_address') {
    questions.push(
      { id: 'address_1', questionType: 'address_1', label: '', placeholder: placeholders.addressPlaceholder, settings: { required: true } },
      { id: 'city', questionType: 'city', label: '', placeholder: placeholders.cityPlaceholder, settings: { required: true } },
      { id: 'postcode', questionType: 'postcode', label: '', placeholder: placeholders.postcodePlaceholder, settings: { required: true } },
    )
  }

  return questions
}

const getValidationMessage = (questionType, translatedValues) => {
  const messageMap = {
    name: translatedValues.validationName,
    email: translatedValues.validationEmail,
    address_1: translatedValues.validationAddress,
    city: translatedValues.validationCity,
    postcode: translatedValues.validationPostcode
  }
  return messageMap[questionType] || translatedValues.validationEnterAResponse
}

export default function EmailDecisionMakers({
  decisionMakers, onSubmit, renderActionTemplate, action, reset, canReset, postcode,
  body, subject, initialBody, setBody, setSubject, setInitialBody, onUpdateNoTarget = () => {}
}) {
  const { fallback: translatedValues } = useActionTranslate({ actionId: action.id })
  const isPreview = useSel(s => s.local.isPreview)
  const targetDetails = useSel(s => s.taker?.targetDetails || {})

  const [loading, setLoading] = useState(true)
  const [selectedDecisionMakers, setSelectedDecisionMakers] = useState([])
  const [showCustomErrors, setShowCustomErrors] = useState(false)
  const [optIn, setOptIn] = useState(false)

  const salutationText = useMemo(() =>
    translatedValues.salutationText?.replace(
      /{{( ?)name( ?)}}/g,
      selectedDecisionMakers.map(id => decisionMakers.find(dm => dm.id === id)?.name).join("/")
    ), [translatedValues.salutationText, selectedDecisionMakers, decisionMakers])

  const emailFormHeaderText = useMemo(() =>
    translatedValues.emailFormHeaderText?.replace(
      /{{( ?)names( ?)}}/g,
      decisionMakers.map(dm => dm.name).join(", ")
    ), [translatedValues.emailFormHeaderText, decisionMakers])

  const questions = getQuestions(action.config.requiredUserInfo, translatedValues)
  const transformedQuestions = transformQuestionsWithTargetDetails(questions, targetDetails, { detailsFirst: true })
  const validationSchema = getValidationSchema(transformedQuestions)
  const isDecisionMakerLookup = !action.config.customDecisionMakers

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      postcode: postcode === 'saved_postcode' ? '' : postcode,
      address_1: '',
      city: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      if (isPreview) return Promise.resolve()
      return onSubmit({
        subject,
        body,
        decisionMakerIds: selectedDecisionMakers,
        optIn: action.config.enableOptIn ? optIn : null,
        ...values
      })
    }
  })

  useEffect(() => {
    const newPostcodeValue = postcode === 'saved_postcode' ? '' : postcode;
    if (formik.values.postcode !== newPostcodeValue) {
      formik.setFieldValue('postcode', newPostcodeValue);
    }
  }, [postcode, formik.setFieldValue]);

  const getCustomErrors = () => {
    const errors = {}
    if (selectedDecisionMakers.length === 0) errors.decisionMakers = translatedValues.selectAtLeastOnePersonValidation
    if (!(subject || '').trim()) errors.subject = translatedValues.subjectRequiredValidation
    if (!(body || '').trim()) errors.body = translatedValues.emailBodyRequiredValidation
    if (action.config.requireBodyEditing === true && body === initialBody) errors.body = translatedValues.editBodyValidationMessage
    return errors
  }

  const handleSubmit = async () => {
    setShowCustomErrors(true)
    const formikErrors = await formik.validateForm()
    formik.setTouched(Object.keys(formik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {}))
    const customErrors = getCustomErrors()

    if (Object.keys(formikErrors).length > 0 || Object.keys(customErrors).length > 0) {
      return Promise.reject()
    }
    return formik.handleSubmit()
  }

  const handleUpdateNoTarget = () => {
    if (isDecisionMakerLookup) reset()
    onUpdateNoTarget()
  }

  const toggleDecisionMaker = (dmId, checked) => {
    setSelectedDecisionMakers(prev =>
      checked ? [...prev, dmId] : prev.filter(id => id !== dmId)
    )
  }

  useEffect(() => {
    if (decisionMakers.length === 1 && selectedDecisionMakers.length === 0) {
      setSelectedDecisionMakers([decisionMakers[0].id])
    }
  }, [decisionMakers, selectedDecisionMakers.length])

  useEffect(() => {
    if (!(body || subject)) { // if already loaded, don't load again
      renderActionTemplate().then(({ data }) => {
        setSubject(data.subject)
        setBody(data.body)
        setInitialBody(data.body)
        setSelectedDecisionMakers(decisionMakers.map(dm => dm.id))
        setLoading(false)
      })
    } else {
      setSelectedDecisionMakers(decisionMakers.map(dm => dm.id))
      setLoading(false)
    }
  }, [])

  if (loading) return <Loading />

  const customErrors = getCustomErrors() // Always calculate custom errors, not just when showCustomErrors is true

  return (
    <div className="email-editor">
      <div className='header'>
        {canReset && <div className='back-caret' onClick={reset}>
          <SvgIconsBackCaret fillOpacity={1} />
        </div>}
        <p className='title'>
          <ActionTextContentField html={emailFormHeaderText} actionId={action.id} slug='emailFormHeaderText' wysiwyg={false} />
        </p>
      </div>

      {decisionMakers.length > 1 && (
        <DecisionMakerSelection
          decisionMakers={decisionMakers}
          selectedDecisionMakers={selectedDecisionMakers}
          onToggle={toggleDecisionMaker}
          error={showCustomErrors ? customErrors.decisionMakers : null}
          action={action}
        />
      )}

      <div className='main-container'>
        {action.config.includeUserInstructions && (
          <div className='left'>
            <ActionTextContentField actionId={action.id} slug='userInstructionsContent'/>
          </div>
        )}

        <div className='right'>
          <EmailFields
            subject={subject}
            setSubject={setSubject}
            body={body}
            setBody={setBody}
            salutationText={salutationText}
            action={action}
            customErrors={customErrors}
            showCustomErrors={showCustomErrors}
            translatedValues={translatedValues}
          />

          <div className={`user-info margin-bottom ${action.config.includeUserInstructions ? 'vertical' : ''}`}>
            {transformedQuestions.map((q) => (
              <QuestionField
                key={q.id}
                question={q}
                formik={formik}
                postcode={postcode}
                isDecisionMakerLookup={isDecisionMakerLookup}
                translatedValues={translatedValues}
                onUpdateNoTarget={handleUpdateNoTarget}
              />
            ))}
          </div>

          {action.config.enableOptIn && (
            <div className='opt-in-checkbox margin-bottom'>
              <label className='flex' style={{ gap: '8px', alignItems: 'center' }}>
                <input
                  type='checkbox'
                  checked={optIn}
                  onChange={e => setOptIn(e.target.checked)}
                  style={{ marginTop: '4px' }}
                />
                <span className="opt-in-label-text">
                  <ActionTextContentField actionId={action.id} slug='optInLabel' wysiwyg={false} />
                </span>
              </label>
            </div>
          )}

          {action.config.includeFooter && (
            <div className="footer" dangerouslySetInnerHTML={{ __html: action.config.footerContent }} />
          )}

          <ServerWaitButton className='margin-top' primary onClick={handleSubmit}>
            <ActionTextContentField actionId={action.id} slug='sendEmailButtonText' wysiwyg={false} />
          </ServerWaitButton>
        </div>
      </div>
    </div>
  )
}

function DecisionMakerSelection({ decisionMakers, selectedDecisionMakers, onToggle, error, action }) {
  return (
    <div className="decision-makers-selection">
      <p className='bold-instruction margin-bottom'>
        <ActionTextContentField actionId={action.id} slug='selectPeopleToEmailText' wysiwyg={false} />
      </p>
      <div className="decision-makers-list">
        {decisionMakers.map((dm) => (
          <label key={dm.id} className="decision-maker-checkbox margin-bottom half">
            <input
              type="checkbox"
              checked={selectedDecisionMakers.includes(dm.id)}
              onChange={(e) => onToggle(dm.id, e.target.checked)}
              disabled={selectedDecisionMakers.includes(dm.id) && selectedDecisionMakers.length === 1} // Disable if last decision maker
            />
            {dm.name}
          </label>
        ))}
      </div>
      {error && <p className="error small">{error}</p>}
    </div>
  )
}

function EmailFields({ subject, setSubject, body, setBody, salutationText, action, customErrors, showCustomErrors, translatedValues }) {
  return (
    <>
      <p className='bold-instruction'>
        <ActionTextContentField actionId={action.id} slug='subjectText' wysiwyg={false}/>
      </p>
      <input
        className={`subject ${!action.config.editableSubject ? 'readonly' : ''}`}
        type='text'
        placeholder={translatedValues.subjectPlaceholder}
        value={subject}
        onChange={e => setSubject(e.target.value || '')}
        readOnly={!action.config.editableSubject}
      />
      {showCustomErrors && customErrors.subject && <p className="error small up-10">{customErrors.subject}</p>}

      <p className='bold-instruction'>
        <ActionTextContentField html={salutationText} actionId={action.id} slug='salutationText' wysiwyg={false}/>
      </p>
      <textarea
        className={`body ${action.config.editableBody === false && !action.config.requireBodyEditing ? 'readonly' : ''}`}
        rows='6'
        value={body}
        onChange={e => setBody(e.target.value || '')}
        readOnly={action.config.editableBody === false && !action.config.requireBodyEditing}
        style={{ resize: 'vertical' }}
      />
      {customErrors.body && (
        (customErrors.body === translatedValues.editBodyValidationMessage) || showCustomErrors
      ) && <p className="error small up-10">{customErrors.body}</p>}

      <p className='bold-instruction margin-bottom'>
        <ActionTextContentField actionId={action.id} slug='emailSignOffText' wysiwyg={false} />
      </p>
    </>
  )
}

function QuestionField({ question, formik, postcode, isDecisionMakerLookup, translatedValues, onUpdateNoTarget }) {
  if (question.questionType === 'detailsMessage') {
    return (
      <div className="field target-details-message">
        <label>{translatedValues.usingSignedInAsLabel} {question.signedInAs}</label>
        <div className='not-me-link' onClick={onUpdateNoTarget}>{translatedValues.notMeText}</div>
      </div>
    )
  }

  if (question.questionType === 'postcode') {
    return <PostcodeField formik={formik} postcode={postcode} isDecisionMakerLookup={isDecisionMakerLookup} translatedValues={translatedValues} />
  }

  if (question.hide) {
    return null
  }

  return <StandardField question={question} formik={formik} translatedValues={translatedValues} />
}

function StandardField({ question, formik, translatedValues }) {
  const value = formik.values[question.id] || ''
  const placeholder = question.settings?.required ? `${question.placeholder}*` : question.placeholder
  const error = formik.errors[question.id]
  const touched = formik.touched[question.id]

  return (
    <div>
      <input
        type='text'
        name={question.id}
        placeholder={placeholder}
        value={value}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
      {touched && error && (
        <p className="error small">
          {getValidationMessage(question.questionType, translatedValues)}
        </p>
      )}
    </div>
  )
}

function PostcodeField({ formik, postcode, isDecisionMakerLookup, translatedValues }) {
  // lookup saved details
  const detailsPresent = useSel(s => s.taker?.targetDetails?.detailsPresent || {})
  const error = formik.errors.postcode
  const touched = formik.touched.postcode

  const getValue = () => {
    if (isDecisionMakerLookup) {
      return postcode === 'saved_postcode' ? detailsPresent.postcodeRedacted || '' : postcode
    }
    return formik.values.postcode || ''
  }

  return (
    <div>
      <input
        type='text'
        name='postcode'
        placeholder={`${translatedValues.postcodePlaceholder}*`}
        value={getValue()}
        disabled={isDecisionMakerLookup}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
      {touched && error && (
        <p className="error small">{getValidationMessage('postcode', translatedValues)}</p>
      )}
    </div>
  )
}
